import React, { useEffect, useState, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { loginRedirect } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, KeyRound, Copy, ArrowLeft } from "lucide-react";

// Two pages in one:
//   /security     → settings hub (enable / disable TOTP, view backup codes count)
//   /security/verify → post-login TOTP gate (renders when user.requires_2fa)

export default function TwoFactor() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [enrollment, setEnrollment] = useState(null); // { provisioning_uri, secret }
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const r = await api.get("/2fa/status");
      setStatus(r.data);
    } catch { setStatus(null); }
  }, []);

  useEffect(() => { if (user) reload(); }, [user, reload]);

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-5 py-16 text-center">
        <h2 className="heading text-3xl font-extrabold mb-3">Please login</h2>
        <Button onClick={loginRedirect} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="twofa-login">Login with Google</Button>
      </div>
    );
  }

  const beginSetup = async () => {
    setBusy(true);
    try {
      const r = await api.post("/2fa/totp/setup");
      setEnrollment(r.data);
      setBackupCodes(null);
      setCode("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Setup failed");
    } finally { setBusy(false); }
  };

  const enable = async () => {
    if (!/^\d{6}$/.test(code)) { toast.error("Enter the 6-digit code from your authenticator app"); return; }
    setBusy(true);
    try {
      const r = await api.post("/2fa/totp/enable", { code });
      setBackupCodes(r.data.backup_codes);
      setEnrollment(null);
      setCode("");
      await reload();
      await checkAuth();
      toast.success("Two-factor authentication enabled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    const c = prompt("Enter a current 6-digit code from your authenticator to disable 2FA:");
    if (!c) return;
    setBusy(true);
    try {
      await api.post("/2fa/totp/disable", { code: c });
      await reload();
      await checkAuth();
      toast.success("2FA disabled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to disable");
    } finally { setBusy(false); }
  };

  const copyCodes = () => {
    if (!backupCodes) return;
    navigator.clipboard.writeText(backupCodes.join("\n"));
    toast.success("Backup codes copied");
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-6" data-testid="twofa-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/account")} data-testid="twofa-back">
          <ArrowLeft size={16} className="mr-1" /> Back
        </Button>
        <h1 className="heading text-2xl font-extrabold">Two-Factor Authentication</h1>
      </div>

      <Card className="border-stone-200">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2">
            {status?.totp_enabled
              ? <><ShieldCheck className="text-emerald-600" size={20} /><span>2FA is <b>ON</b></span></>
              : <><ShieldAlert className="text-amber-600" size={20} /><span>2FA is <b>OFF</b></span></>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-stone-700">
          <p>Two-factor authentication adds an extra step every time you sign in — a rotating 6-digit code from an app like Google Authenticator, Authy or 1Password.</p>
          {status?.totp_enabled && (
            <div className="text-xs text-stone-500" data-testid="twofa-status-details">
              Enrolled: {status.totp_enrolled_at ? new Date(status.totp_enrolled_at).toLocaleString() : "—"}
              {" · "}Backup codes remaining: <b>{status.backup_codes_left}</b>
            </div>
          )}
          <div className="pt-2 flex flex-wrap gap-2">
            {!status?.totp_enabled ? (
              <Button onClick={beginSetup} disabled={busy} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="twofa-begin-setup">
                {busy ? "Working…" : "Enable 2FA"}
              </Button>
            ) : (
              <Button onClick={disable} disabled={busy} variant="outline" className="rounded-full" data-testid="twofa-disable">
                Disable 2FA
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {enrollment && (
        <Card className="border-stone-200" data-testid="twofa-enrollment">
          <CardHeader><CardTitle>Scan the QR code</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-center bg-white p-4 rounded-xl">
              <QRCodeSVG value={enrollment.provisioning_uri} size={200} />
            </div>
            <div className="text-xs text-stone-500 text-center">
              Can't scan? Paste this key into your app: <code className="ml-1 px-2 py-0.5 bg-stone-100 rounded font-mono" data-testid="twofa-manual-secret">{enrollment.secret}</code>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Enter the 6-digit code from your app</label>
              <div className="flex gap-2">
                <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456" inputMode="numeric" maxLength={6} className="text-center text-lg font-mono tracking-widest" data-testid="twofa-enable-code" />
                <Button onClick={enable} disabled={busy || code.length !== 6} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="twofa-enable-submit">
                  Verify & Enable
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {backupCodes && (
        <Card className="border-amber-300 bg-amber-50" data-testid="twofa-backup-codes">
          <CardHeader><CardTitle className="flex items-center gap-2"><KeyRound size={18} /> Save your backup codes</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-amber-900">Each code works once. Store them somewhere safe — you'll need one if you ever lose your authenticator app.</p>
            <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-white p-3 rounded-lg border border-amber-200">
              {backupCodes.map((c) => <div key={c} className="text-center py-1">{c}</div>)}
            </div>
            <Button variant="outline" size="sm" onClick={copyCodes} className="rounded-full gap-2" data-testid="twofa-copy-codes">
              <Copy size={14} /> Copy all
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


export function TwoFactorGate({ onVerified }) {
  const [code, setCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [busy, setBusy] = useState(false);
  const { logout } = useAuth();

  const submit = async () => {
    const trimmed = (code || "").trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      await api.post("/2fa/totp/verify", { code: trimmed, use_backup: useBackup });
      toast.success("Verified");
      onVerified?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="twofa-gate">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ShieldCheck className="text-[#E05E36]" size={20} /> Two-Factor Verification</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-stone-600">
            {useBackup
              ? "Enter one of your 8-character backup codes."
              : "Enter the 6-digit code from your authenticator app to continue."}
          </p>
          <Input
            value={code}
            onChange={(e) => {
              const v = useBackup ? e.target.value.trim() : e.target.value.replace(/\D/g, "").slice(0, 6);
              setCode(v);
            }}
            placeholder={useBackup ? "e.g. a1b2c3d4" : "123456"}
            inputMode={useBackup ? "text" : "numeric"}
            className="text-center text-lg font-mono tracking-widest"
            data-testid="twofa-gate-code"
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
          <Button onClick={submit} disabled={busy || !code} className="w-full rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="twofa-gate-submit">
            {busy ? "Verifying…" : "Verify"}
          </Button>
          <div className="flex justify-between text-xs pt-2">
            <button className="text-[#E05E36] hover:underline" onClick={() => { setUseBackup(!useBackup); setCode(""); }} data-testid="twofa-toggle-backup">
              {useBackup ? "Use authenticator code" : "Use backup code"}
            </button>
            <button className="text-stone-500 hover:underline" onClick={() => { logout(); window.location.href = "/"; }} data-testid="twofa-gate-logout">
              Sign out
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
